import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportFilename } from "@/lib/timefmt";
import { reportImportErrors } from "@/lib/importUtils";
import { toast } from "sonner";
import { Upload, Send, Users, Download } from "lucide-react";

function splitCSVLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
      else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const header = splitCSVLine(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z]/g, ""));
  const hasHeader = header.includes("email");
  const idx = (names, fallback) => {
    const i = header.findIndex((h) => names.includes(h));
    return i >= 0 ? i : fallback;
  };
  const ci = {
    ext: idx(["externalid", "externaluserid"], 0), email: idx(["email", "emailaddress"], 1),
    fn: idx(["firstname"], 2), ln: idx(["lastname"], 3),
    unit: idx(["unit"], 4), sub: idx(["subunit"], 5), team: idx(["team"], 6),
    role: idx(["role"], 7),
  };
  return (hasHeader ? lines.slice(1) : lines).map((l) => {
    const c = splitCSVLine(l);
    return {
      external_user_id: c[ci.ext] || "", email: (c[ci.email] || "").toLowerCase(),
      first_name: c[ci.fn] || "", last_name: c[ci.ln] || "",
      unit: c[ci.unit] || "", subunit: c[ci.sub] || "", team: c[ci.team] || "",
      role: c[ci.role] || "",
    };
  }).filter((r) => r.email);
}

export default function InviteTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [rows, setRows] = useState([]);
  const [emailsText, setEmailsText] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orgTree, setOrgTree] = useState(null);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => setOrgTree(r.data)).catch(() => {});
    api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadTemplate = () => {
    const t = orgTree?.terminology || { unit_label: "Unit", subunit_label: "Subunit", team_label: "Team" };
    const u = orgTree?.units?.[0];
    const s = u?.subunits?.[0];
    const tm = s?.teams?.[0];
    const header = `ExternalID,Email,First Name,Last Name,${t.unit_label},${t.subunit_label},${t.team_label},Role`;
    const sample = (ext, email, fn, ln) => [ext, email, fn, ln, u?.name || "", s?.name || "", tm?.name || "", roles[0]?.name || ""].join(",");
    const csv = [header, sample("EX-1001", "jane.doe@example.com", "Jane", "Doe"), sample("EX-1002", "john.smith@example.com", "John", "Smith")].join("\n") + "\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename("crew-ready-invite-template", "csv", user);
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(String(reader.result || ""));
      setRows(parsed);
      setResults(null);
      if (!parsed.length) toast.error("No valid rows found (need an email column)");
      else toast.success(`${parsed.length} row(s) ready to invite`);
    };
    reader.readAsText(f);
  };

  const addEmailList = () => {
    const emails = emailsText.split(/[,\n]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!emails.length) { toast.error("Enter at least one email"); return; }
    setRows(emails.map((email) => ({ email, external_user_id: "", first_name: "", last_name: "", unit: "", subunit: "", team: "", role: "" })));
    setResults(null);
  };

  const send = async () => {
    if (!rows.length) { toast.error("Nothing to send"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { company_id: companyId, rows });
      setResults(data.results);
      const ok = data.results.filter((r) => r.status.startsWith("invited") || r.status.startsWith("created")).length;
      toast.success(`${ok} invite(s) processed`);
      const errs = data.results.filter((r) => r.status.startsWith("error")).map((r) => ({ line: r.line, error: r.status.replace(/^error:\s*/, "") }));
      if (errs.length) reportImportErrors({ errors: errs }, "invites", user);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-4 w-4 text-slate-400" />
          <div className="font-heading text-lg font-semibold">Invite Users</div>
        </div>
        <p className="text-xs text-slate-500">
          Each invite emails the person an initial password — they must set a new password at first sign-in.
          {user?.admin_level === "unit" && " Invited members are placed inside your unit."}
        </p>
        {isSuper && (
          <div className="mt-4 max-w-sm">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="invite-company-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="invite-csv-card">
          <div className="font-heading text-base font-semibold mb-1">CSV upload</div>
          <p className="text-xs text-slate-500 mb-3 font-mono-num">ExternalID, Email, First Name, Last Name, Unit, Subunit, Team, Role (optional)</p>
          <label className="flex items-center justify-center gap-2 px-4 py-6 rounded-md border border-dashed border-slate-700 hover:border-blue-500 cursor-pointer transition-colors text-sm text-slate-300">
            <Upload className="h-4 w-4" /> Choose CSV file
            <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="invite-csv-input" className="hidden" />
          </label>
          <button onClick={downloadTemplate} data-testid="invite-template-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm transition-colors">
            <Download className="h-4 w-4" /> Download template (with sample rows)
          </button>
        </div>
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="invite-emails-card">
          <div className="font-heading text-base font-semibold mb-1">Email list</div>
          <p className="text-xs text-slate-500 mb-3">Emails separated by commas (no org placement)</p>
          <textarea value={emailsText} onChange={(e) => setEmailsText(e.target.value)} rows={3}
            data-testid="invite-emails-input" placeholder="jane@unit.mil, john@unit.mil"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm font-mono-num" />
          <button onClick={addEmailList} data-testid="invite-emails-load"
            className="mt-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm transition-colors">Load emails</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="invite-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-base font-semibold">{rows.length} row(s) ready</div>
            <button onClick={send} disabled={busy} data-testid="invite-send-btn"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-60">
              <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send invites"}
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-left">
                  {["Ext ID", "Email", "First", "Last", "Unit", "Subunit", "Team", "Role"].map((h) => (
                    <th key={h} className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {rows.map((r, i) => (
                  <tr key={r.email} data-testid={`invite-row-${i}`}>
                    <td className="px-2 py-1.5 font-mono-num">{r.external_user_id || "—"}</td>
                    <td className="px-2 py-1.5">{r.email}</td>
                    <td className="px-2 py-1.5">{r.first_name || "—"}</td>
                    <td className="px-2 py-1.5">{r.last_name || "—"}</td>
                    <td className="px-2 py-1.5">{r.unit || "—"}</td>
                    <td className="px-2 py-1.5">{r.subunit || "—"}</td>
                    <td className="px-2 py-1.5">{r.team || "—"}</td>
                    <td className="px-2 py-1.5">{r.role || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {results && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="invite-results">
          <div className="font-heading text-base font-semibold mb-3">Results</div>
          <div className="space-y-1.5">
            {results.map((r, i) => (
              <div key={r.email} className="flex items-center justify-between text-sm" data-testid={`invite-result-${i}`}>
                <span className="font-mono-num">{r.email}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-num border ${
                  r.status === "invited" ? "rag-green"
                  : r.status === "exists" ? "rag-yellow"
                  : r.status.startsWith("created") ? "rag-amber" : "rag-red"}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
